// app/src/lib/flow.jsx — the create-flow NAVIGATION state (CREATE-2). The server-side DRAFT
// (CREATE-1 §5) is now the single source of truth for content AND resume; this context only
// holds navigation glue:
//   • draftId — WHICH draft the create screens (Ideas → Script → Generating) work on. Persisted
//     as a pointer (navigation state, never draft content) so re-opening /create/* recovers it.
//   • pick — the transient chosen card ({ setJobId, cardId }) handed from Ideas to Script for the
//     FIRST script generation. In-memory only; on resume Script reads the draft's recorded
//     chosen_idea instead. Lost on reload = the user re-picks from Ideas (no spend lost).
//
// The old localStorage draft (prompt / chosen idea / step / aspect) is RETIRED — GET
// /v1/drafts?active=1 is the cross-device resume spine (D-REVIEW finding 7, fixed properly).

import React, { createContext, useContext, useState, useEffect } from 'react';

const KEY = 'qs.draftId';
const FlowCtx = createContext(null);

export function FlowProvider({ children }) {
  const [draftId, setId] = useState(() => {
    try { return localStorage.getItem(KEY) || null; } catch { return null; }
  });
  const [pick, setPick] = useState(null); // { setJobId, cardId } — transient, not persisted
  useEffect(() => {
    try { if (draftId) localStorage.setItem(KEY, draftId); else localStorage.removeItem(KEY); }
    catch { /* private mode — the id lives in memory for this session */ }
  }, [draftId]);
  // switching (or clearing) the draft drops any transient pick — a pick belongs to the draft
  // it was chosen in (CATCHUP F1 enabler: `clear()` had zero callers, so a stale pick could
  // survive a draft switch and 400 the script route; the re-pick fix leans on this being clean).
  const setDraftId = (id) => { setId(id || null); setPick(null); };
  const clearPick = () => setPick(null); // Script calls this once its pick is durably recorded (chosen_idea)
  const clear = () => { setId(null); setPick(null); };
  return <FlowCtx.Provider value={{ draftId, setDraftId, pick, setPick, clearPick, clear }}>{children}</FlowCtx.Provider>;
}

export function useFlow() {
  return useContext(FlowCtx);
}
